import React from "react";
import { useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  EnvelopeIcon,
  HashtagIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckSolid } from "@heroicons/react/24/solid";

import TransactionDetail from "../../shared/components/TransactionDetail";

import * as API from "../../api";

/**
 * Payment confirmation page — shown after the bank paywall redirects back
 * on success. Premium Tailwind redesign, mirrors screens/Booking/thankyou.js
 * (same emerald success circle, RecapCell grid, two-column summary layout)
 * so paying reads as the natural next step after the thank-you page.
 *
 * Logic preserved exactly: calcPrices, the roaders lookup, the
 * API.TransactionDetail(order.id) call, the missing order/auth redirect-home
 * guard, the loading state, the "no transaction found" fallback state, and
 * every field previously displayed (transaction id, email, total paid,
 * status via the statuses map, payment method).
 */
const PaymentSuccess = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const order = JSON.parse(localStorage.getItem("order_response")) || {};
  // Read fresh from localStorage rather than the module-level API.AuthStorage
  // snapshot — that constant is captured once when the JS bundle first
  // parses, so it stays empty for the rest of the SPA session if the user
  // authenticated later (e.g. inline during checkout). Using the stale
  // snapshot here made this page redirect home / show "no transaction found"
  // even for a genuinely successful, fully-processed payment.
  const auth = JSON.parse(localStorage.getItem("auth")) || {};
  const [totalPrice, setTotalPrice] = React.useState(0);
  const [totalPriceReduc, setTotalPriceReduc] = React.useState(0);
  const [totalPriceRest, setTotalPriceRest] = React.useState(0);
  const [loading, seLoading] = React.useState(true);
  const [totalPricePay, setTotalPricePay] = React.useState(0);
  const [transactionDetail, setTransactionDetail] = React.useState(null);
  const [road, setRoad] = React.useState(null);

  const calcPrices = (order) => {
    let _totalPrice = 0;
    order.prices.forEach((price) => {
      let p = parseInt(price.price) * parseInt(price.qt ?? price.quantities);
      _totalPrice = _totalPrice + p;
    });

    let _percentageSettings = 100 - order.percentage_paid;
    let _total = _totalPrice - (_totalPrice / 100) * (parseInt(order.coupon_value) ? parseInt(order.coupon_value) : parseInt(order.customer_discount));
    let _forPay = _total - (_total / 100) * _percentageSettings;
    let _forRest = _total - _forPay;
    setTotalPrice(_totalPrice);
    setTotalPriceReduc(_total);
    setTotalPriceRest(_forRest);
  };

  const roaders = [
    { id: 1, value: 0, name: "Je ne suis pas motorisé" },
    { id: 2, value: 3, name: "Je suis motorisé, j’ai 3 places libres" },
    { id: 3, value: 2, name: "Je suis motorisé, j’ai 2 places libres" },
    { id: 4, value: 1, name: "Je suis motorisé, j’ai une place libres" },
    { id: 5, value: 0, name: "Je suis motorisé, je n’ai aucune place libre" },
  ];

  const statuses = {
    'AUTHORIZE_PENDING': 'En attente',
    'AUTHORIZED': 'Autorisé',
    'AUTH_REVERSED': 'Inversement',
    'CANCELLED': 'Annulée',
    'DECLINED': 'Refusé',
    'CHARGE_PENDING': 'Charge en cours',
    'CHARGED': 'Facturé',
    'CHARGED_BACK': 'Remboursé',
    'CHARGEBACK_REVERSED': 'Inversement remboursé',
    'CHARGEBACK_PENDING': 'Remboursement en attente',
    'REFUND_PENDING': 'Remboursement en attente',
    'REFUNDED': 'Remboursé',
    'ERROR': 'Erreur',
    'CREDITED_PENDING': 'Crédit en attente',
    'CREDITED': 'Crédité'
  }; // Outputs: "Autorisé"

  const totalPlace = (_prices) => {
    let places = 0;
    _prices.forEach((price) => {
      places = parseInt(places) + parseInt(price.qt ?? price.quantities);
    });
    return places;
  };

  React.useEffect(() => {
    if (!Object.keys(order).length || !Object.keys(auth).length) {
      history.replace('/')
    }
    calcPrices(order);
    if (roaders.some((r) => r.id == parseInt(order.roader))) {
      setRoad(roaders.find((r) => r.id == parseInt(order.roader)).name);
    }

    if (order && order.id && !transactionDetail) {
      API.TransactionDetail(order.id).then((res) => {
        let transaction = res.data;
        setTransactionDetail(transaction);
        // format the transaction.total_amount tow number after the decimal point
        setTotalPricePay(parseFloat(transaction.total_amount).toFixed(2));
      }).catch((err) => {
        console.log(err);
      }).finally(() => {
        seLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!Object.keys(order).length || !Object.keys(auth).length) return null;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 px-4 text-center">
        <svg className="h-9 w-9 animate-spin text-brand-500" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
        </svg>
        <p className="text-sm text-ink-muted">{t('payment.loadingTitle')}</p>
      </div>
    );
  }

  if (!transactionDetail || !order) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-container flex-col items-center justify-center px-4 py-20 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-amber-50">
          <InformationCircleIcon className="h-10 w-10 text-amber-500" />
        </div>
        <h1 className="mt-6 font-display text-2xl font-bold text-ink sm:text-3xl">
          {t('payment.notFoundTitle')}
        </h1>
        <p className="mt-2.5 max-w-md text-sm text-ink-muted sm:text-base">
          {t('payment.notFoundBody')}
        </p>
        <Link
          to="/"
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
        >
          {t('payment.notFoundCta')}
        </Link>
      </div>
    );
  }

  const email = auth?.user?.email;
  const status = statuses[transactionDetail?.status] ?? t('payment.statuses.pending');
  const paymentMethod = `${t('payment.creditCard')}${transactionDetail?.payment_details?.paymentMethod ? ", " + transactionDetail.payment_details.paymentMethod : ""}`;

  return (
    <div className="bg-surface-muted pb-12">
      {/* Success hero */}
      <header className="relative overflow-hidden border-b border-slate-100 bg-surface">
        <div className="mx-auto flex w-full max-w-container flex-col items-center px-4 py-12 text-center sm:px-6 sm:py-16 lg:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckSolid className="h-10 w-10 text-emerald-500" />
          </span>
          <h1 className="mt-5 max-w-xl font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {t('payment.successTitle')}
          </h1>
          <p className="mt-2.5 text-sm text-ink-muted">{t('payment.successSubtitle')}</p>
        </div>
      </header>

      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* Left: transaction recap */}
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
              <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-slate-100 sm:grid-cols-2">
                <RecapCell
                  icon={HashtagIcon}
                  label={t('payment.transactionId')}
                  value={`#${transactionDetail?.payment_details?.id ?? order.id ?? "-"}`}
                />
                <RecapCell icon={EnvelopeIcon} label={t('payment.email')} value={email} />
                <RecapCell icon={CreditCardIcon} label={t('payment.totalPaid')} value={API.priceFormat(totalPricePay)} />
                <RecapCell icon={CheckCircleIcon} label={t('payment.status')} value={status} />
                <RecapCell
                  icon={BuildingLibraryIcon}
                  label={t('payment.paymentMethod')}
                  value={paymentMethod}
                  className="sm:col-span-2"
                />
              </div>
            </section>

            <Link
              to="/account/orders"
              className="inline-flex items-center gap-2 rounded-full border border-brand-500 px-5 py-2.5 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
            >
              {t('payment.viewBookings')}
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          {/* Right: order summary */}
          <aside>
            <div className="lg:sticky lg:top-24">
              <TransactionDetail
                transactionDetail={transactionDetail}
                orderInfo={order}
                title={t('payment.summaryTitle')}
                price={API.priceFormat(totalPrice)}
                departCity={order.depart_city}
                carpool={road}
                qte={totalPlace(order.prices)}
                total={API.priceFormat(totalPriceReduc)}
                totalPay={totalPricePay}
                theRest={API.priceFormat(totalPriceRest)}
                zeroPaid={API.priceFormat(totalPricePay) === "0,00 MAD" ? false : true}
                prices={false}
                userDiscount={
                  parseInt(order.customer_discount) &&
                  { name: 'user', value: order.customer_discount, price: API.priceFormat(totalPriceReduc - totalPrice) }
                }
                coupon={
                  parseInt(order.coupon_value) &&
                  { value: order.coupon_value, name: order.coupon_name, price: API.priceFormat(totalPriceReduc - totalPrice) }
                }
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ---- helpers ----------------------------------------------------------------

function RecapCell({ icon: Icon, label, value, className = '' }) {
  return (
    <div className={`flex items-center gap-3 bg-white px-4 py-3.5 ${className}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
        <p className="truncate text-sm font-semibold text-ink">{value || "—"}</p>
      </div>
    </div>
  );
}

export default PaymentSuccess;
